import { Activity } from '@cube3/common/model/schema/resources/activities';
import {
  useActivityContext,
  useActivityContextWithPath
} from './useActivityContext';
import { useActivityActor } from './useActivityActor';
import { useActivitySubject } from './useActivitySubject';
import {
  getActivityImage,
  getActivityImageType
} from '../utils/getActivityImage';
import { useMemo } from 'react';
import { getChangedValue, getPrep, getVerb } from '../utils/getActivityConfig';
import { Share } from '@cube3/common/model/schema/resources/share';
import { logoFiles } from '@cube3/cubicle/src/core/atoms/BrandLogo/files';

import { ActivityImageType } from '@cube3/cubicle/src/core/molecules/Activity/ActivityItem';
import { useActivityClickBehavior } from './useActivityClickBehavior';
import { capitalize } from '@material-ui/core';

/** Generate activity feed in asset detail view */
export const useAssetActivityFeed = (
  activity: Activity,
  onClose?: () => void
) => {
  const { action, created_at: date } = activity;
  const { subject, actor, related_resources: context } = activity.relationships;

  const {
    resource: contextResource,
    name,
    share: shareFromContext
  } = useActivityContext(context);

  const { name: actorName, image: actorAvatar } = useActivityActor(actor);
  const {
    name: subjectName,
    resource: subjectResource,
    shareIntent,
    thread: resolvedComment
  } = useActivitySubject(subject, actor?.id);

  const sourceImage = getActivityImage({
    resource: subjectResource || subject,
    context: contextResource || {},
    action: action
  });

  const { name: contextName, type: contextType } =
    useActivityContextWithPath(activity);

  // #region share related activities
  // for the share activities, we want to display share details,
  // e.g.: `user commented on this asset in 'share'`,

  const isShareLink = subject?.type === 'share';
  const share = (isShareLink ? subjectResource : shareFromContext) as Share;
  /** click the <share.display_name> will open the share settings modal, */

  const isComment = subject?.type === 'comment';
  const isApproval = subject?.type === 'approval';
  const prevApprovalStatus = isApproval && activity?.['status'];

  const {
    visitingAsset,
    visitObject,
    visitContext,
    visitShareSettings,
    visitReviewLink
  } = useActivityClickBehavior({
    activity,
    onClose
  });

  //#region Generate message
  const imageSection = useMemo(() => {
    const type = getActivityImageType(action, 'asset');
    const hasBrandLogo = !!logoFiles[sourceImage];
    return {
      type: (type === 'brand'
        ? hasBrandLogo
          ? 'brand'
          : 'icon' // will use `export` icon as fallback
        : type) as ActivityImageType,
      image: type === 'avatar' ? actorAvatar : sourceImage,
      name: actorName
    };
  }, [action, actorAvatar, sourceImage]);

  const { topRowSection, bottomRowSection } = useMemo(() => {
    let topRowSection = [];
    let bottomRowSection = [];

    /** The object that was modified */
    const what = {
      name: subjectName,
      href: undefined,
      onClick:
        share && action !== 'AssetPreviewed'
          ? visitShareSettings
          : visitObject()
    };
    /** The actor */
    const who = {
      name: actorName
    };
    /** Context */
    const where = (contextName || shareFromContext) && {
      name: shareFromContext ? shareFromContext.display_name : contextName,
      icon: shareFromContext ? 'link' : contextType,
      herf: undefined,
      onClick: shareFromContext ? visitShareSettings : visitContext
    };
    const verb = getVerb(
      activity,
      isShareLink
        ? shareIntent
        : isComment || isApproval
        ? subjectResource
        : undefined
    );
    const prep = getPrep(action);

    bottomRowSection = [
      date,
      where && (shareFromContext ? 'in' : prep),
      shareFromContext && capitalize(shareFromContext.intent || ''),
      where
    ];

    // {Who} did {what}, at {time} in {where}
    switch (action) {
      case 'ContractCreated':
        topRowSection = [
          { ...who, maxWidth: 240 },
          'created',
          {
            ...what,
            maxWidth: 240
          },
          'for',
          where
        ];
        bottomRowSection = [date];
        break;
      case 'ContractDeleted':
        topRowSection = [
          { ...who, maxWidth: 240 },
          'deleted',
          {
            ...what,
            maxWidth: 240
          },
          'attached to',
          where
        ];
        bottomRowSection = [date];
        break;
      case 'ExportSuccess': {
        const exportTarget = name;
        topRowSection = [
          { ...who, maxWidth: 240 },
          'exported',
          {
            ...what,
            maxWidth: 240
          },
          exportTarget && 'to',
          exportTarget && { name: exportTarget, maxWidth: 120 }
        ];
        bottomRowSection = [date];
        break;
      }
      case 'ExportFailed': {
        const exportTarget = name;
        topRowSection = [
          'Export of',
          {
            ...what,
            maxWidth: 240
          },
          exportTarget && 'to',
          exportTarget && { name: exportTarget, maxWidth: 120 },
          'by',
          { ...who, maxWidth: 240 },
          'failed'
        ];
        bottomRowSection = [date];
        break;
      }
      // shares
      case 'ApprovalCreated':
        topRowSection = [
          { ...who, maxWidth: 240 },
          prevApprovalStatus || verb,
          { ...what, name: visitingAsset?.display_name }
        ];
        break;
      case 'ApprovalUpdated':
        topRowSection = [
          { ...who, maxWidth: 240 },
          verb,
          'for',
          { ...what, name: visitingAsset?.display_name }
        ];
        break;
      case 'ApprovalDeleted':
        topRowSection = [
          { ...who, maxWidth: 240 },
          'cleared',
          { ...what, name: visitingAsset?.display_name, maxWidth: 240 },
          "'s approval state"
        ];

        break;
      case 'CommentUpdated':
      case 'CommentResolved':
      case 'CommentDeleted':
      case 'CommentCreated':
        topRowSection = [
          { ...who, maxWidth: 240 },
          resolvedComment ? 'replied comment' : verb,
          prep,
          { ...what, name: visitingAsset?.display_name }
        ];

        break;
      case 'AssetDownloaded': {
        const downloadType = contextResource?.display_name;
        topRowSection = [
          { ...who, maxWidth: 240 },
          verb,
          { ...what, maxWidth: 240 },
          'as',
          { name: downloadType }
        ];

        break;
      }
      case 'AssetUpdated': {
        const { oldVal, newVal } = getChangedValue(activity);
        topRowSection = [
          { ...who, maxWidth: 240 },
          'changed',
          { ...what, name: oldVal, maxWidth: 240 },
          'to',
          { ...what, name: newVal, maxWidth: 240 }
        ];
        break;
      }
      default:
        topRowSection = [{ ...who, maxWidth: 240 }, verb, what];
        break;
    }

    return { topRowSection, bottomRowSection };
  }, [
    share,
    visitShareSettings,
    date,
    activity,
    subjectName,
    contextName,
    contextType,
    visitObject,
    visitContext
  ]);

  return {
    imageSection,
    topRowSection,
    bottomRowSection,
    onDefaultClick: share ? visitReviewLink() : undefined,
    onClickDataSection:
      isComment || isApproval ? visitReviewLink(true) : undefined,
    subjectResource,
    resolvedComment
  };
};
